import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { Box, Typography, CircularProgress, LinearProgress } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useTheme } from "@mui/styles";
import { useDropzone } from "react-dropzone";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import sh from "bundles/common/utils/sh";
import { uploadJobOfferMedia } from "bundles/common/utils/uploadJobOfferMedia";

export const MAX_MEDIA_FILES = 12;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024; // 100 MB, mirrors JobOffer::MAX_VIDEO_BYTE_SIZE

// `staged`, `onStageFiles` and `onRemoveStaged` are owned by the parent
// (JobOfferEdit) so background uploads survive step navigation; the parent
// starts each upload as soon as a file is staged. This component only renders
// the staged tiles (with progress) and, in edit mode (persisted offer),
// uploads immediately to the existing record.
const JobOfferMediaFields = ({
  jobOffer,
  onChange,
  staged = [],
  onStageFiles,
  onRemoveStaged,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const authenticityToken = useSelector((state) => state.authenticity_token);

  const [images, setImages] = useState(jobOffer?.job_offer_images_records || []);
  const [videos, setVideos] = useState(jobOffer?.job_offer_videos_records || []);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Re-seed local state when the job offer's media loads/changes — e.g. when the
  // edit page finishes fetching the full offer after the initial (media-less) mount.
  useEffect(() => {
    setImages(jobOffer?.job_offer_images_records || []);
    setVideos(jobOffer?.job_offer_videos_records || []);
  }, [
    jobOffer?.id,
    jobOffer?.job_offer_images_records,
    jobOffer?.job_offer_videos_records,
  ]);

  const persisted = Boolean(jobOffer?.id);
  const totalCount = images.length + videos.length + staged.length;

  const syncParent = (nextImages, nextVideos) => {
    if (onChange) {
      onChange({
        job_offer_images_records: nextImages,
        job_offer_videos_records: nextVideos,
      });
    }
  };

  // Edit mode: the offer already exists, so upload straight to the record.
  const handleUpload = async (files, attributeName) => {
    const remainingSlots = MAX_MEDIA_FILES - totalCount;
    if (remainingSlots <= 0) {
      enqueueSnackbar(
        intl.formatMessage({ id: "job_offer_creation.media.max_reached" }),
        { variant: "error" }
      );
      return;
    }

    setUploading(true);
    setProgress(0);
    const filesToUpload = files.slice(0, remainingSlots);
    let uploadedAny = false;
    for (const file of filesToUpload) {
      try {
        setProgress(0);
        const res = await uploadJobOfferMedia(
          file,
          jobOffer.id,
          attributeName,
          authenticityToken,
          (pct) => setProgress(pct)
        );
        const nextImages = res.job_offer.job_offer_images_records || [];
        const nextVideos = res.job_offer.job_offer_videos_records || [];
        setImages(nextImages);
        setVideos(nextVideos);
        syncParent(nextImages, nextVideos);
        uploadedAny = true;
      } catch (err) {
        enqueueSnackbar(
          intl.formatMessage({ id: "job_offer_creation.media.upload_failed" }),
          { variant: "error" }
        );
      }
    }
    setUploading(false);
    setProgress(0);
    if (uploadedAny) {
      enqueueSnackbar(
        intl.formatMessage({ id: "job_offer_creation.media.uploaded" }),
        { variant: "success" }
      );
    }
  };

  const handleDelete = (attachmentId) => {
    sh.put("/job_offer/purge_media", {
      authenticity_token: authenticityToken,
      media: { id: attachmentId },
    })
      .then((res) => {
        const nextImages = res.data.job_offer.job_offer_images_records || [];
        const nextVideos = res.data.job_offer.job_offer_videos_records || [];
        setImages(nextImages);
        setVideos(nextVideos);
        syncParent(nextImages, nextVideos);
        enqueueSnackbar(
          intl.formatMessage({ id: "job_offer_creation.media.deleted" }),
          { variant: "success" }
        );
      })
      .catch(() => {
        enqueueSnackbar(
          intl.formatMessage({ id: "job_offer_creation.media.upload_failed" }),
          { variant: "error" }
        );
      });
  };

  // Creation mode: hand the files to the parent to stage + upload in the
  // background. Enforce the shared cap here since we know the full count.
  const stageFiles = (files, kind) => {
    const remainingSlots = MAX_MEDIA_FILES - totalCount;
    if (remainingSlots <= 0) {
      enqueueSnackbar(
        intl.formatMessage({ id: "job_offer_creation.media.max_reached" }),
        { variant: "error" }
      );
      return;
    }
    onStageFiles(files.slice(0, remainingSlots), kind);
  };

  const addMedia = (files, attributeName) => {
    const kind = attributeName === "job_offer_videos" ? "video" : "image";
    if (persisted) {
      handleUpload(files, attributeName);
    } else {
      stageFiles(files, kind);
    }
  };

  const photosDropzone = useDropzone({
    accept: [".png", ".jpeg", ".jpg", ".webp"],
    disabled: uploading || totalCount >= MAX_MEDIA_FILES,
    onDrop: (acceptedFiles) => addMedia(acceptedFiles, "job_offer_images"),
  });

  const videosDropzone = useDropzone({
    accept: [".mp4", ".webm", ".mov", ".m4v", ".ogv"],
    maxSize: MAX_VIDEO_BYTES,
    disabled: uploading || totalCount >= MAX_MEDIA_FILES,
    onDrop: (acceptedFiles) => addMedia(acceptedFiles, "job_offer_videos"),
    onDropRejected: (rejections) => {
      const tooLarge = rejections.some((rejection) =>
        rejection.errors?.some((error) => error.code === "file-too-large")
      );
      if (tooLarge) {
        enqueueSnackbar(
          intl.formatMessage({ id: "job_offer_creation.media.video_too_large" }),
          { variant: "error" }
        );
      }
    },
  });

  const thumbSx = {
    position: "relative",
    width: "120px",
    height: "90px",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid hsl(160, 10%, 90%)",
    "& img, & video": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
    "& .delete-button": {
      position: "absolute",
      top: "4px",
      right: "4px",
      background: "rgba(255, 255, 255, 0.85)",
      borderRadius: "50%",
      width: "20px",
      height: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "rgba(0, 0, 0, 0.7)",
      zIndex: 2,
    },
  };

  const addTile = (dropzone, labelId) => (
    <Box
      {...dropzone.getRootProps()}
      sx={{
        width: "120px",
        height: "90px",
        borderRadius: "8px",
        border: "1px dashed",
        borderColor:
          totalCount >= MAX_MEDIA_FILES
            ? "hsl(160, 10%, 85%)"
            : theme.palette.secondary.main,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        cursor: totalCount >= MAX_MEDIA_FILES ? "not-allowed" : "pointer",
        color:
          totalCount >= MAX_MEDIA_FILES
            ? "hsl(160, 5%, 60%)"
            : theme.palette.secondary.main,
      }}
    >
      <input {...dropzone.getInputProps()} />
      <AddCircleIcon fontSize="medium" />
      <Typography variant="body3">
        <FormattedMessage id={labelId} />
      </Typography>
    </Box>
  );

  // A staged (not-yet-attached) tile: preview + background-upload state overlay.
  const stagedTile = (item) => (
    <Box key={item.id} sx={thumbSx}>
      {item.kind === "video" ? (
        <>
          <video src={item.previewUrl} muted />
          <PlayCircleOutlineIcon
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              color: "rgba(255,255,255,0.9)",
              fontSize: "32px",
              pointerEvents: "none",
            }}
          />
        </>
      ) : (
        <img src={item.previewUrl} alt="Selected media" />
      )}

      {item.status === "uploading" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.45)",
          }}
        >
          <CircularProgress
            variant={item.progress > 0 ? "determinate" : "indeterminate"}
            value={item.progress}
            size={26}
            sx={{ color: "#fff" }}
          />
        </Box>
      )}

      {item.status === "error" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(211, 47, 47, 0.35)",
            color: "#fff",
          }}
        >
          <ErrorOutlineIcon />
        </Box>
      )}

      <Box className="delete-button" onClick={() => onRemoveStaged(item.id)}>
        <CloseIcon sx={{ fontSize: "14px" }} />
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden", boxSizing: "border-box" }}
      align="left"
    >
      <Typography
        sx={{
          fontFamily: "Segoe UI Bold, system-ui, sans-serif",
          fontSize: { xs: "1.125rem", md: "1.375rem" },
          color: "hsl(178, 93%, 10%)",
          letterSpacing: "-0.02em",
        }}
      >
        <FormattedMessage id="job_offer_creation.media.title" />
      </Typography>
      <Typography variant="body1" sx={{ mt: "8px", mb: "16px" }}>
        <FormattedMessage id="job_offer_creation.media.subtitle" />
      </Typography>

      {/* Photos */}
      <Typography variant="h5" sx={{ mb: "8px" }}>
        <FormattedMessage id="job_offer_creation.media.photos" />
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", mb: "24px" }}>
        {addTile(photosDropzone, "job_offer_creation.media.add_photos")}
        {images.map((record, index) => (
          <Box key={`img-${index}`} sx={thumbSx}>
            <img src={record.url} alt={`Job offer photo ${index + 1}`} />
            <Box
              className="delete-button"
              onClick={() => handleDelete(record.picture?.id)}
            >
              <CloseIcon sx={{ fontSize: "14px" }} />
            </Box>
          </Box>
        ))}
        {staged.filter((item) => item.kind === "image").map(stagedTile)}
      </Box>

      {/* Videos */}
      <Typography variant="h5" sx={{ mb: "8px" }}>
        <FormattedMessage id="job_offer_creation.media.videos" />
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
        {addTile(videosDropzone, "job_offer_creation.media.add_videos")}
        {videos.map((record, index) => (
          <Box key={`vid-${index}`} sx={thumbSx}>
            <video src={record.url} muted />
            <PlayCircleOutlineIcon
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "rgba(255,255,255,0.9)",
                fontSize: "32px",
                pointerEvents: "none",
              }}
            />
            <Box
              className="delete-button"
              onClick={() => handleDelete(record.video?.id)}
            >
              <CloseIcon sx={{ fontSize: "14px" }} />
            </Box>
          </Box>
        ))}
        {staged.filter((item) => item.kind === "video").map(stagedTile)}
      </Box>

      {uploading && (
        <Box sx={{ mt: "16px", maxWidth: "320px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <CircularProgress
              variant={progress > 0 && progress < 100 ? "determinate" : "indeterminate"}
              value={progress}
              size={18}
            />
            <Typography variant="body2">
              <FormattedMessage id="loading" />
              {progress > 0 ? ` ${progress}%` : ""}
            </Typography>
          </Box>
          <LinearProgress
            variant={progress > 0 && progress < 100 ? "determinate" : "indeterminate"}
            value={progress}
            sx={{ mt: "8px", borderRadius: "4px" }}
          />
        </Box>
      )}

      {totalCount === 0 && !uploading && (
        <Typography variant="body2" sx={{ mt: "16px", color: "hsl(160, 5%, 55%)" }}>
          <FormattedMessage id="job_offer_creation.media.none" />
        </Typography>
      )}
    </Box>
  );
};

export default JobOfferMediaFields;
